import { captureException } from '@sentry/core';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import * as Y from 'yjs';
import { useMutationNotepadStateUpdate } from '../mutations/useMutationNotepadStateUpdate.js';
import { resourceDetailsQueryKeys } from './resourceDetailsQueryKeys.js';
import { getDatabase, getPersisterQueriesStoreName } from '@fabric-app/compat';
import { useWoody } from '../../woody/woody.js';
import { isQueryEnabled } from '@fabric-app/queries/utilities';
import { createLogger } from '@fabric-app/utils/logging';
import { useContextAccessToken } from '../../permission/contexts/AnonShareTokenContext.js';
import { isDefined, ensureError } from '@fabric-app/utils/guards';

const useQueryNotepadResourceState = (resource, queryOptions) => {
  const queryClient = useQueryClient();
  const { client } = useWoody();
  const enableMergeResync = queryOptions?.enableMergeResync ?? true;
  const mutationUpdateNotepadState = useMutationNotepadStateUpdate({
    resourceId: resource?.id
  });
  const accessToken = useContextAccessToken();
  const query = useQuery({
    queryKey: resourceDetailsQueryKeys.notepadState(resource),
    queryFn: async (params) => {
      const safeResourceId = params.queryKey[1];
      const logger = createLogger(`notepad-state-${safeResourceId}`);
      const response = await client.v2({
        endpoint: "/v2/notepads/{resourceId}/state",
        params: {
          resourceId: safeResourceId
        }
      }, {
        query: {
          accessToken
        }
      });
      try {
        const key = resourceDetailsQueryKeys.notepadState(resource);
        const oldData = (
          // Try to get it directly from react query if available
          queryClient.getQueryData(key) ?? // Otherwise we fall back to getting it directly from the local DB since it could have been GC'ed
          (await getDatabase()?.getItem(getPersisterQueriesStoreName(), `tanstack-query-${JSON.stringify(key)}`))?.state.data
        );
        const newData = new Uint8Array(await response.arrayBuffer());
        if (!oldData || !isDefined(oldData) || !Array.isArray(oldData) || oldData.length === 0 || !enableMergeResync || resource?.isReadonly) {
          logger.log("No resource state stored, using fresh remote value.");
          return newData;
        }
        try {
          logger.log("Merging remote state with locally stored one.");
          const mergedData = Y.mergeUpdates([
            oldData,
            newData
          ]);
          const diff = Y.diffUpdate(mergedData, newData);
          if (diff.length > 0 && resource) {
            logger.log("Diff detected between local<>remote state, sending diff to remote.");
            mutationUpdateNotepadState.mutate({
              update: diff,
              resource
            });
          }
          return mergedData;
        } catch (e) {
          captureException(new Error("Unable to merge YJS data, discarding local cached version."), {
            extra: {
              resourceId: resource?.id,
              originalError: ensureError(e)
            }
          });
          logger.error("Unable to merge YJS data, discarding local cached version.");
          return newData;
        }
      } catch (e) {
        captureException(new Error("Something went wrong when parsing the remote notepad state, or local cached version is corrupted."), {
          extra: {
            resourceId: resource?.id,
            originalError: ensureError(e)
          }
        });
        logger.error("Something went wrong when parsing the remote notepad state, or local cached version is corrupted.");
        throw e;
      }
    },
    staleTime: 0,
    refetchInterval: false,
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    ...queryOptions,
    enabled: isQueryEnabled([
      !!resource?.id,
      queryOptions?.enabled
    ])
  });
  return {
    ...query
  };
};

export { useQueryNotepadResourceState };
//# sourceMappingURL=useQueryNotepadResourceState.js.map
